import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { gql } from "apollo-boost";
import { graphql, compose } from "react-apollo";
import { Flex, Box } from "grid-styled";
import { amountToCents, formatPrice } from "../utils/helpers";
import Text from "./Text";
import Modal from "./Modal";
import Button from "./Button";
import ContractsList from "./ContractsList";
import ContractForm from "./ContractForm";
import JobQuery from "../queries/Job";
import JobContractsQuery, { fragments } from "../queries/JobContracts";
import UpdateJobMutation from "../mutations/UpdateJob";
import CreateContractMutation from "../mutations/CreateContract";
import { addToCache } from "../utils/queryHelpers";
import CenteredLoader from "./CenteredLoader";

class Contracts extends React.Component {
  state = {
    showingNewContractModal: false,
    editingContractId: null,
    cancellingContractId: null,
    uncancellingContractId: null
  };

  editContract = contract => {
    this.setState({ editingContractId: contract.id });
  };

  cancelContract = contract => {
    this.setState({ cancellingContractId: contract.id });
  };

  uncancelContract = contract => {
    this.setState({ uncancellingContractId: contract.id });
  };

  handleCreate = ({
    contractNumber,
    contractType,
    amount,
    ...contractValues
  }) => {
    this.props.updateJob({
      input: {
        contractNumber,
        contractType
      }
    });
    this.props
      .createContract({
        input: { ...contractValues, amountInCents: amountToCents(amount) }
      })
      .then(() => {
        // Add flash message
        this.setState({ showingNewContractModal: false });
      });
  };

  handleUpdate = ({
    contractType,
    contractNumber,
    amount,
    ...contractValues
  }) => {
    this.props.updateJob({
      input: {
        contractNumber,
        contractType
      }
    });
    this.props
      .updateContract({
        id: this.state.editingContractId,
        input: { ...contractValues, amountInCents: amountToCents(amount) }
      })
      .then(() => {
        // TODO: Add flash message
        this.setState({ editingContractId: null });
      });
  };

  handleCancel = () => {
    const contract = this.props.contracts.find(
      contract => contract.id === this.state.cancellingContractId
    );
    if (contract) {
      this.props.cancelContract({ id: contract.id }).then(() => {
        // TODO: Add flash message
        this.setState({ cancellingContractId: null });
      });
    }
  };

  handleUncancel = () => {
    const contract = this.props.contracts.find(
      contract => contract.id === this.state.uncancellingContractId
    );
    if (contract) {
      this.props.uncancelContract({ id: contract.id }).then(() => {
        // TODO: Add flash message
        this.setState({ uncancellingContractId: null });
      });
    }
  };

  renderNewContractModal = () => {
    const close = () => this.setState({ showingNewContractModal: false });
    return (
      <Modal
        isOpen={!!this.state.showingNewContractModal}
        title="New contract"
        close={close}
      >
        <Box p={2}>
          <ContractForm
            job={this.props.job}
            onSubmit={this.handleCreate}
            cancel={close}
          />
        </Box>
      </Modal>
    );
  };

  renderEditContractModal = () => {
    const close = () => this.setState({ editingContractId: null });
    return (
      <Modal
        isOpen={!!this.state.editingContractId}
        title="Editing a contract..."
        close={close}
      >
        <Box p={2}>
          <ContractForm
            job={this.props.job}
            contract={this.props.contracts.find(
              contract => contract.id === this.state.editingContractId
            )}
            onSubmit={this.handleUpdate}
            cancel={close}
          />
        </Box>
      </Modal>
    );
  };

  renderCancelContractModal = () => {
    const close = () => this.setState({ cancellingContractId: null });
    const contract = this.state.cancellingContractId
      ? this.props.contracts.find(
          contract => contract.id === this.state.cancellingContractId
        )
      : null;
    return (
      <Modal isOpen={!!contract} title="Cancelling a contract..." close={close}>
        <Box p={2}>
          <Text style={{ marginTop: 0 }}>
            Are you sure you want to cancel this{" "}
            {contract && <b>{formatPrice(contract.amountInCents)}</b>} contract
          </Text>
          <Flex justify="flex-end" mt={2}>
            <Button variant="danger" onClick={this.handleCancel}>
              Cancel Contract
            </Button>
          </Flex>
        </Box>
      </Modal>
    );
  };

  renderUncancelContractModal = () => {
    const close = () => this.setState({ uncancellingContractId: null });
    const contract = this.state.uncancellingContractId
      ? this.props.contracts.find(
          contract => contract.id === this.state.uncancellingContractId
        )
      : null;
    return (
      <Modal
        isOpen={!!contract}
        title="Uncancelling a contract..."
        close={close}
      >
        <Box p={2}>
          <Text style={{ marginTop: 0 }}>
            Are you sure you want to uncancel this{" "}
            {contract && <b>{formatPrice(contract.amountInCents)}</b>} contract
          </Text>
          <Flex justify="flex-end" mt={2}>
            <Button variant="danger" onClick={this.handleUncancel}>
              Uncancel Contract
            </Button>
          </Flex>
        </Box>
      </Modal>
    );
  };

  render() {
    const { loading, error, contracts, job } = this.props;

    if (loading) return <CenteredLoader />;
    if (error) return "Error.";

    return (
      <Box pt={3}>
        <Flex justify="flex-end" pb={2}>
          <Button
            onClick={() => this.setState({ showingNewContractModal: true })}
            variant="primary"
          >
            New {contracts.length ? "Addendum" : "Contract"}
          </Button>
        </Flex>
        <ContractsList
          contractType={job.contractType}
          primaryContractId={job.primaryContractId}
          contracts={contracts}
          editContract={this.editContract}
          setAsPrimaryContract={this.props.setAsPrimaryContract}
          cancelContract={this.cancelContract}
          uncancelContract={this.uncancelContract}
        />
        {this.renderNewContractModal()}
        {this.renderEditContractModal()}
        {this.renderCancelContractModal()}
        {this.renderUncancelContractModal()}
      </Box>
    );
  }

  static propTypes = {
    job: PropTypes.object.isRequired,
    loading: PropTypes.bool.isRequired,
    contracts: PropTypes.array
  };
}

const UpdateContractMutation = gql`
  mutation UpdateContract($id: ID!, $input: ContractInput!) {
    updateContract(id: $id, input: $input) {
      ...JobContractsContract
    }
  }
  ${fragments.Contract}
`;

const CancelContractMutation = gql`
  mutation CancelContract($id: ID!) {
    cancelContract(id: $id) {
      ...JobContractsContract
    }
  }
  ${fragments.Contract}
`;

const UncancelContractMutation = gql`
  mutation UncancelContract($id: ID!) {
    uncancelContract(id: $id) {
      ...JobContractsContract
    }
  }
  ${fragments.Contract}
`;

const SetPrimaryContractMutation = gql`
  mutation SetPrimaryContract($id: ID!, $contractId: ID!) {
    setPrimaryContract(id: $id, contractId: $contractId) {
      id
      contractNumber
      contractType
      primaryContractId
      contact {
        id
        firstName
        lastName
        otherFirstName
        otherLastName
      }
      franchise {
        id
        name
      }
    }
  }
`;

const enhance = compose(
  graphql(JobContractsQuery, {
    options: ({ job }) => ({
      variables: { jobId: job.id }
    }),
    props: ({ data: { loading, error, contracts } }) => ({
      loading,
      error,
      contracts
    })
  }),
  graphql(UpdateJobMutation, {
    props: ({ ownProps: { job }, mutate }) => ({
      updateJob({ input }) {
        return mutate({
          variables: {
            id: job.id,
            input
          }
        });
      }
    })
  }),
  graphql(CreateContractMutation, {
    props: ({ ownProps: { job }, mutate }) => ({
      createContract({ input }) {
        return mutate({
          variables: {
            input: {
              ...input,
              amountInCents: +input.amountInCents,
              effectiveDate: moment(input.effectiveDate).format("YYYY-MM-DD"),
              jobId: job.id
            }
          },
          refetchQueries: [
            {
              query: JobQuery,
              variables: { jobId: job.id }
            }
          ],
          update: (store, { data: { createContract } }) => {
            addToCache(createContract, store, "contracts", {
              query: JobContractsQuery,
              variables: { jobId: job.id }
            });
          }
        });
      }
    })
  }),
  graphql(UpdateContractMutation, {
    props: ({ mutate }) => ({
      updateContract({ id, input }) {
        return mutate({
          variables: {
            id,
            input: {
              ...input,
              amountInCents: +input.amountInCents,
              effectiveDate: moment(input.effectiveDate).format("YYYY-MM-DD")
            }
          }
        });
      }
    })
  }),
  graphql(CancelContractMutation, {
    props: ({ mutate }) => ({
      cancelContract({ id }) {
        return mutate({
          variables: { id }
        });
      }
    })
  }),
  graphql(UncancelContractMutation, {
    props: ({ mutate }) => ({
      uncancelContract({ id }) {
        return mutate({
          variables: { id }
        });
      }
    })
  }),
  graphql(SetPrimaryContractMutation, {
    props: ({ ownProps: { job }, mutate }) => ({
      setAsPrimaryContract(contract) {
        return mutate({
          variables: {
            id: job.id,
            contractId: contract.id
          }
        });
      }
    })
  })
);

export default enhance(Contracts);



// WEBPACK FOOTER //
// ./src/components/Contracts.js